import React from 'react';
import { Box, Typography, Fab, Zoom, IconButton } from '@mui/material';
import { ShoppingCart, Close, CancelOutlined } from '@mui/icons-material';
import { useMenuStore } from '../../stores/menuStore.js';

// Height exported so MobileLayout can calculate overlay size
export const FLOATING_BUTTON_HEIGHT = 48;

/**
 * Floating "New Order" / "Close" button.
 * - Shows "New Order" + item count + total + cross (clear cart) when cart has items and overlay is closed.
 * - Shows "Close" when the cart overlay is open.
 * - Hidden when cart is empty and overlay is closed.
 * - Stuck to the right side of the screen.
 */
export const FloatingCartButton = ({ onClick, isOpen }) => {
  const cart = useMenuStore((s) => s.cart);
  const clearCart = useMenuStore((s) => s.clearCart);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const isVisible = isOpen || cartItemCount > 0;

  const handleClearCart = (e) => {
    e.stopPropagation(); // prevent triggering the main onClick
    clearCart();
  };

  return (
    <Zoom in={isVisible} unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 'calc(60px + env(safe-area-inset-bottom, 0px))',
          right: 16,
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.75,
        }}
      >
        {/* Cross button to clear cart — only when overlay is closed and cart has items */}
        {!isOpen && cartItemCount > 0 && (
          <IconButton
            size="small"
            onClick={handleClearCart}
            sx={{
              width: 40,
              height: 40,
              bgcolor: '#FF3B30',
              color: 'rgba(255, 255, 255, 0.72)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              '&:hover': { bgcolor: '#D70015' },
              '&:active': { transform: 'scale(0.9)' },
              transition: 'all 0.15s ease',
            }}
          >
            <CancelOutlined sx={{ fontSize: 24 }} />
          </IconButton>
        )}

        <Fab
          variant="extended"
          onClick={onClick}
          sx={{
            bgcolor: isOpen ? '#FF3B30' : '#4527A0',
            color: 'rgba(255, 255, 255, 0.72)',
            fontWeight: 700,
            fontSize: '0.88rem',
            borderRadius: '28px',
            px: 3,
            py: 1,
            minHeight: FLOATING_BUTTON_HEIGHT,
            textTransform: 'none',
            boxShadow: isOpen
              ? '0 6px 24px rgba(255, 59, 48, 0.35)'
              : '0 6px 24px rgba(69, 39, 160, 0.35)',
            '&:hover': { bgcolor: isOpen ? '#D70015' : '#311B92' },
            '&:active': { transform: 'scale(0.96)' },
            gap: 1,
            transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
          }}
        >
          {isOpen ? (
            <>
              <Close sx={{ fontSize: 20 }} />
              <Typography
                component="span"
                sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'inherit' }}
              >
                Close
              </Typography>
            </>
          ) : (
            <>
              <ShoppingCart sx={{ fontSize: 20 }} />
              {/* <Typography
                component="span"
                sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'inherit' }}
              >
                New
              </Typography> */}
              <Box
                sx={{
                  bgcolor: 'secondary.main',
                  borderRadius: '14px',
                  px: 1,
                  py: 0.15,
                  ml: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontWeight: 800, fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.72)' }}
                >
                  {cartItemCount}
                </Typography>
                <Typography
                  component="span"
                  sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'rgba(255,255,255,0.85)' }}
                >
                  · ₹{cartTotal.toFixed(0)}
                </Typography>
              </Box>
            </>
          )}
        </Fab>
      </Box>
    </Zoom>
  );
};
